import { normalizeChartMetricNumber } from "../components/ChatPage/ChatSectionChartHelpers";
import { formatChartAxisDateValue } from "./formatChartAxisDate";
import {
  buildColumnKeyMapping,
  getTableColumnSchema,
  normalizeTableRows,
} from "./normalizeTableRows";

const SERIES_COLUMN_HINTS =
  /month|year_?month|yearmonth|report_?month|billing_?month|period|quarter|yr_?mn|yyyymm|compare|fiscal/i;

const EXPLICIT_SERIES_FIELDS = [
  "series_column",
  "group_column",
  "color_column",
  "legend_column",
  "series_key",
  "hue_column",
];

function parseChartCellValue(value, asNumeric = false) {
  if (value == null) return asNumeric ? NaN : value;
  if (typeof value === "number" && Number.isFinite(value)) {
    return asNumeric ? value : value;
  }
  const str = String(value).trim();
  if (!asNumeric) return str;
  const parsed = parseFloat(str.replace(/[₹$€,\s]/g, ""));
  return Number.isFinite(parsed) ? parsed : NaN;
}

function formatSeriesLabel(value) {
  if (value == null) return "";
  const dateFormatted = formatChartAxisDateValue(value);
  if (dateFormatted !== value) return String(dateFormatted);
  return String(value);
}

function seriesDataKey(index) {
  return `series_${index}`;
}

/** Unique values in first-seen order. */
function uniqueInOrder(values) {
  const seen = new Set();
  const out = [];
  for (const v of values) {
    const key = String(v);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(v);
  }
  return out;
}

export function detectSeriesColumn(rawResults, xColumn, yColumn, chart = {}) {
  if (!rawResults?.length || !xColumn || !yColumn) return null;

  for (const field of EXPLICIT_SERIES_FIELDS) {
    const col = chart?.[field];
    if (!col || col === xColumn || col === yColumn) continue;
    const resolved = findRowColumnKey(rawResults, col);
    if (resolved && rawResults[0]?.[resolved] !== undefined) {
      return resolved;
    }
  }

  const cols = Object.keys(rawResults[0]).filter((c) => c !== xColumn && c !== yColumn);

  for (const col of cols) {
    if (!SERIES_COLUMN_HINTS.test(col)) continue;
    const uniques = new Set(rawResults.map((r) => r[col]));
    if (uniques.size >= 2 && uniques.size <= 24) return col;
  }

  for (const col of cols) {
    const uniques = new Set(rawResults.map((r) => r[col]));
    if (uniques.size < 2 || uniques.size > 24) continue;

    const perX = new Map();
    for (const row of rawResults) {
      const x = row[xColumn];
      if (!perX.has(x)) perX.set(x, new Set());
      perX.get(x).add(String(row[col]));
    }
    if ([...perX.values()].some((set) => set.size > 1)) return col;
  }

  return null;
}

function buildSingleSeriesData(rawResults, xColumn, yColumn) {
  const data = rawResults.map((row) => {
    let xVal = parseChartCellValue(row[xColumn], false);
    if (typeof xVal === "string") {
      const parsedX = parseFloat(xVal.replace(/[₹$€,]/g, ""));
      if (!Number.isNaN(parsedX)) xVal = parsedX;
    }
    const yVal = parseChartCellValue(row[yColumn], true);
    return {
      xValue: xVal,
      yValue: normalizeChartMetricNumber(yVal),
    };
  });
  return { data, seriesKeys: null, isMultiSeries: false };
}

function buildMultiSeriesData(rawResults, xColumn, yColumn, seriesColumn) {
  const seriesValues = uniqueInOrder(rawResults.map((r) => r[seriesColumn]));
  const xValues = uniqueInOrder(rawResults.map((r) => r[xColumn]));

  const seriesKeys = seriesValues.map((sv, index) => ({
    dataKey: seriesDataKey(index),
    label: formatSeriesLabel(sv),
  }));

  const data = xValues.map((xRaw) => {
    let xValue = parseChartCellValue(xRaw, false);
    if (typeof xValue === "string") {
      const parsedX = parseFloat(xValue.replace(/[₹$€,]/g, ""));
      if (!Number.isNaN(parsedX)) xValue = parsedX;
    }

    const point = { xValue };
    seriesValues.forEach((sv, index) => {
      const row = rawResults.find(
        (r) => String(r[xColumn]) === String(xRaw) && String(r[seriesColumn]) === String(sv)
      );
      const yVal = row != null ? parseChartCellValue(row[yColumn], true) : NaN;
      point[seriesDataKey(index)] = Number.isFinite(yVal)
        ? normalizeChartMetricNumber(yVal)
        : null;
    });
    return point;
  });

  return { data, seriesKeys, isMultiSeries: true, seriesColumn };
}

/** Map chart column name to an actual key present on message/history rows. */
function findRowColumnKey(rows, columnName) {
  if (!columnName || !rows?.length) return columnName;
  const sample = rows[0];
  if (sample && Object.prototype.hasOwnProperty.call(sample, columnName)) {
    return columnName;
  }
  const schema = getTableColumnSchema(rows);
  const mapping = buildColumnKeyMapping(schema, [columnName]);
  const mapped = mapping[columnName];
  if (mapped && sample?.[mapped] !== undefined) return mapped;
  const lower = String(columnName).toLowerCase();
  const caseMatch = schema.find((k) => String(k).toLowerCase() === lower);
  if (caseMatch && sample?.[caseMatch] !== undefined) return caseMatch;
  return mapped || columnName;
}

function resolveChartColumnsForRows(rows, chart) {
  const resolved = { ...chart };
  if (chart.x_column) resolved.x_column = findRowColumnKey(rows, chart.x_column);
  if (chart.y_column) resolved.y_column = findRowColumnKey(rows, chart.y_column);
  for (const field of EXPLICIT_SERIES_FIELDS) {
    if (chart[field]) resolved[field] = findRowColumnKey(rows, chart[field]);
  }
  return resolved;
}

export function buildChartDataFromRawResults(rawResults, chart) {
  if (!rawResults?.length || !chart?.x_column || !chart?.y_column) {
    return { data: [], seriesKeys: null, isMultiSeries: false };
  }

  const seriesColumn = detectSeriesColumn(
    rawResults,
    chart.x_column,
    chart.y_column,
    chart
  );

  if (!seriesColumn) {
    return buildSingleSeriesData(rawResults, chart.x_column, chart.y_column);
  }

  return buildMultiSeriesData(
    rawResults,
    chart.x_column,
    chart.y_column,
    seriesColumn
  );
}

function transformMultiSeriesFromApi(chartData, visualizationChart) {
  const labels = chartData.labels;
  if (!Array.isArray(labels) || labels.length === 0) return null;

  let seriesList = chartData.series || chartData.datasets || chartData.groups;
  if (!Array.isArray(seriesList) || seriesList.length < 2) return null;

  const seriesKeys = seriesList.map((s, index) => ({
    dataKey: seriesDataKey(index),
    label: formatSeriesLabel(s.name ?? s.label ?? s.title ?? `Series ${index + 1}`),
  }));

  const data = labels.map((label, idx) => {
    const point = { xValue: label };
    seriesList.forEach((s, sIdx) => {
      const values = s.display_values ?? s.values ?? s.data ?? [];
      const raw = values[idx];
      const n = normalizeChartMetricNumber(parseChartCellValue(raw, true));
      point[seriesDataKey(sIdx)] = Number.isFinite(n) ? n : null;
    });
    return point;
  });

  const rawType = visualizationChart.type ?? visualizationChart.chart_type;
  const typeNorm =
    rawType == null ? null : String(rawType).trim().toLowerCase();

  return {
    type: typeNorm || rawType,
    x_column: visualizationChart.x_column,
    y_column: visualizationChart.y_column,
    title: visualizationChart.title,
    data,
    seriesKeys,
    isMultiSeries: true,
    normalization: visualizationChart.normalization,
    formattedValues: chartData.formatted_values,
  };
}

/**
 * Transform visualization API chart format to component-compatible format.
 * Supports single-series (labels + display_values) and multi-series (data.series[]).
 */
export function transformVisualizationChart(visualizationChart) {
  if (!visualizationChart?.data) return null;

  const chartData = visualizationChart.data;
  const multi = transformMultiSeriesFromApi(chartData, visualizationChart);
  if (multi) return multi;

  if (!chartData.labels) return null;

  const data = chartData.labels.map((label, idx) => ({
    xValue: label,
    yValue: normalizeChartMetricNumber(chartData.display_values?.[idx]),
  }));

  const rawType = visualizationChart.type ?? visualizationChart.chart_type;
  const typeNorm =
    rawType == null ? null : String(rawType).trim().toLowerCase();

  return {
    type: typeNorm || rawType,
    x_column: visualizationChart.x_column,
    y_column: visualizationChart.y_column,
    title: visualizationChart.title,
    data,
    seriesKeys: null,
    isMultiSeries: false,
    normalization: visualizationChart.normalization,
    formattedValues: chartData.formatted_values,
  };
}

/**
 * Build Recharts-ready chart configs from suggested_charts + optional raw_results.
 * Prefers serialized visualization payloads (Messages API) over rebuilding from rows.
 */
export function enrichChartsFromRawResults(rawResults, charts) {
  if (!Array.isArray(charts) || !charts.length) return [];

  const rows =
    Array.isArray(rawResults) && rawResults.length > 0
      ? normalizeTableRows(rawResults, null)
      : null;

  return charts.map((chart) => {
    const fromViz = transformVisualizationChart(chart);
    if (fromViz && getValidChartPoints(fromViz).length > 0) {
      return { ...chart, ...fromViz };
    }

    const existing = {
      ...chart,
      isMultiSeries: chart.isMultiSeries ?? Boolean(chart.seriesKeys?.length),
    };
    if (Array.isArray(chart.data) && getValidChartPoints(existing).length > 0) {
      return existing;
    }

    if (!rows) {
      return {
        ...chart,
        data: Array.isArray(chart.data) ? chart.data : [],
        seriesKeys: chart.seriesKeys ?? null,
        isMultiSeries: Boolean(chart.seriesKeys?.length),
      };
    }

    const resolvedChart = resolveChartColumnsForRows(rows, chart);
    const built = buildChartDataFromRawResults(rows, resolvedChart);
    const merged = { ...chart, ...resolvedChart, ...built };
    if (getValidChartPoints(merged).length > 0) return merged;

    // Rebuild may fail on column mismatch; keep API visualization if present
    if (fromViz) return { ...chart, ...fromViz };
    return merged;
  });
}

/** Filter chart points for rendering (single- or multi-series). */
export function isChartPointValid(point, chart, chartType = "") {
  if (point?.xValue == null) return false;

  if (chart?.isMultiSeries && chart?.seriesKeys?.length) {
    return chart.seriesKeys.some((sk) => {
      const v = point[sk.dataKey];
      return v != null && typeof v === "number" && !Number.isNaN(v);
    });
  }

  const hasY =
    point.yValue != null &&
    typeof point.yValue === "number" &&
    !Number.isNaN(point.yValue);
  if (!hasY) return false;

  if (chartType === "scatter") {
    return (
      point.xValue != null &&
      typeof point.xValue === "number" &&
      !Number.isNaN(point.xValue)
    );
  }

  return true;
}

/** First non-empty suggested_charts array on a message (null/[] → no charts). */
export function resolveSuggestedChartsFromMessage(msg = {}, vizData = null) {
  const vd = vizData ?? msg.metadata?.visualization_data ?? msg.visualization_data;
  const candidates = [
    vd?.suggested_charts,
    msg?.visualization_data?.suggested_charts,
    msg?.suggested_charts,
    msg?.chart_suggestions,
  ];
  for (const value of candidates) {
    if (Array.isArray(value) && value.length > 0) return value;
  }
  return null;
}

/** Points that can actually be rendered for a chart config. */
export function getValidChartPoints(chart) {
  if (!chart) return [];
  const chartTypeRaw = String(chart.type || chart.chart_type || "").toLowerCase();
  const rawData = Array.isArray(chart.data) ? chart.data : [];
  return rawData.filter((d) => isChartPointValid(d, chart, chartTypeRaw));
}

export function hasDisplayableCharts(charts) {
  return Array.isArray(charts) && charts.some((c) => getValidChartPoints(c).length > 0);
}

export function filterDisplayableCharts(charts) {
  if (!Array.isArray(charts)) return [];
  return charts.filter((c) => getValidChartPoints(c).length > 0);
}
